import OrdersIcon from "@/components/icons/chapters/OrdersIcon";
import TransportIcon from "@/components/icons/chapters/TransportIcon";
import Skeleton from "@/components/Skeleton/Skeleton";
import { useGetFacility } from "@/hooks/useFacilities";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Link } from "react-router-dom";
import useFacilityId from "../../hooks/useFacilityId";
import styles from "../FacilityDetail.module.scss";
import { useDashboardYear } from "@/pages/dashboard/hooks/useDateRange";

export default function FacilityRelations() {
  const { year } = useDashboardYear();
  const facilityId = useFacilityId();

  const { data: facility } = useGetFacility(facilityId);

  const facilityRelations = useQuery({
    queryKey: ["facilities", "detail", facilityId, "relations", year],
    enabled: !!year,
    queryFn: () =>
      axios
        .get<{
          po_arrival_count: number;
          so_arrival_count: number;
          po_departure_count: number;
          so_departure_count: number;
          inbound_arrival_count: number;
          outbound_arrival_count: number;
          inbound_departure_count: number;
          outbound_departure_count: number;
        }>("/facilities/" + facilityId + `/relations`, { params: { year } })
        .then((x) => x.data),
  });

  return (
    <div className={styles.relations}>
      <span className={styles.title}>Relations</span>
      <span className={styles.subtitle}>
        <OrdersIcon /> Orders
      </span>
      <div className={styles.wrapper}>
        <Link
          to={`/orders/purchase_orders?search_departure_facility_name=${
            facility?.facility_name || ""
          }`}
          className={styles.relation}
        >
          <div className={styles.relationHeader}>Purchase departure</div>
          <div className={styles.metric}>
            <Skeleton
              loading={facilityRelations.isLoading}
              style={{ width: 20, height: 24 }}
            >
              {facilityRelations.data?.po_departure_count}
            </Skeleton>
          </div>
        </Link>
        <Link
          to={`/orders/purchase_orders?search_arrival_facility_name=${
            facility?.facility_name || ""
          }`}
          className={styles.relation}
        >
          <div className={styles.relationHeader}>Purchase arrival</div>
          <div className={styles.metric}>
            <Skeleton
              loading={facilityRelations.isLoading}
              style={{ width: 20, height: 24 }}
            >
              {facilityRelations.data?.po_arrival_count}
            </Skeleton>
          </div>
        </Link>
        <Link
          to={`/orders/sales_orders?search_departure_facility_name=${
            facility?.facility_name || ""
          }`}
          className={styles.relation}
        >
          <div className={styles.relationHeader}>Sales departure</div>
          <div className={styles.metric}>
            <Skeleton
              loading={facilityRelations.isLoading}
              style={{ width: 20, height: 24 }}
            >
              {facilityRelations.data?.so_departure_count}
            </Skeleton>
          </div>
        </Link>
        <Link
          to={`/orders/sales_orders?search_arrival_facility_name=${facility?.facility_name || ""}`}
          className={styles.relation}
        >
          <div className={styles.relationHeader}>Sales arrival</div>
          <div className={styles.metric}>
            <Skeleton
              loading={facilityRelations.isLoading}
              style={{ width: 20, height: 24 }}
            >
              {facilityRelations.data?.so_arrival_count}
            </Skeleton>
          </div>
        </Link>
      </div>
      <span className={styles.subtitle}>
        <TransportIcon /> Transport routes
      </span>
      <div className={styles.wrapper}>
        <Link
          to={`/transport/inbound?search_departure_name=${facility?.facility_name || ""}`}
          className={styles.relation}
        >
          <div className={styles.relationHeader}>Inbound departure</div>
          <div className={styles.metric}>
            <Skeleton
              loading={facilityRelations.isLoading}
              style={{ width: 20, height: 24 }}
            >
              {facilityRelations.data?.inbound_departure_count}
            </Skeleton>
          </div>
        </Link>
        <Link
          to={`/transport/inbound?search_arrival_name=${facility?.facility_name || ""}`}
          className={styles.relation}
        >
          <div className={styles.relationHeader}>Inbound arrival</div>
          <div className={styles.metric}>
            <Skeleton
              loading={facilityRelations.isLoading}
              style={{ width: 20, height: 24 }}
            >
              {facilityRelations.data?.inbound_arrival_count}
            </Skeleton>
          </div>
        </Link>
        <Link
          to={`/transport/outbound?search_departure_name=${facility?.facility_name || ""}`}
          className={styles.relation}
        >
          <div className={styles.relationHeader}>Outbound departure</div>
          <div className={styles.metric}>
            <Skeleton
              loading={facilityRelations.isLoading}
              style={{ width: 20, height: 24 }}
            >
              {facilityRelations.data?.outbound_departure_count}
            </Skeleton>
          </div>
        </Link>
        <Link
          to={`/transport/outbound?search_arrival_name=${facility?.facility_name || ""}`}
          className={styles.relation}
        >
          <div className={styles.relationHeader}>Outbound arrival</div>
          <div className={styles.metric}>
            <Skeleton
              loading={facilityRelations.isLoading}
              style={{ width: 20, height: 24 }}
            >
              {facilityRelations.data?.outbound_arrival_count}
            </Skeleton>
          </div>
        </Link>
      </div>
    </div>
  );
}
